import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Context-aware pre-filled WhatsApp messages (5 languages × 4 contexts).
// Selected automatically based on current page so the customer just hits send.
const TEMPLATES = {
  general: {
    de: "Hallo Han Kung Restaurant, ich habe eine Frage:",
    en: "Hello Han Kung Restaurant, I have a question:",
    fr: "Bonjour Han Kung Restaurant, j'ai une question :",
    it: "Salve Han Kung Restaurant, ho una domanda:",
    zh: "您好，漢宮酒樓，我有個問題想請教：",
  },
  reservation: {
    de: "Hallo Han Kung Restaurant, ich möchte einen Tisch reservieren für …",
    en: "Hello Han Kung Restaurant, I'd like to reserve a table for …",
    fr: "Bonjour Han Kung Restaurant, je souhaite réserver une table pour …",
    it: "Salve Han Kung Restaurant, vorrei prenotare un tavolo per …",
    zh: "您好，漢宮酒樓，我想訂位，人數及時間如下：",
  },
  takeaway: {
    de: "Hallo Han Kung Restaurant, ich möchte eine Take-Away Bestellung aufgeben:",
    en: "Hello Han Kung Restaurant, I'd like to place a take-away order:",
    fr: "Bonjour Han Kung Restaurant, je souhaite commander à emporter :",
    it: "Salve Han Kung Restaurant, vorrei effettuare un ordine d'asporto:",
    zh: "您好，漢宮酒樓，我想訂購外賣：",
  },
  menu: {
    de: "Hallo Han Kung Restaurant, eine Frage zur Speisekarte:",
    en: "Hello Han Kung Restaurant, a question about the menu:",
    fr: "Bonjour Han Kung Restaurant, une question sur la carte :",
    it: "Salve Han Kung Restaurant, una domanda sul menù:",
    zh: "您好，漢宮酒樓，關於菜單想請教：",
  },
};

const TOOLTIPS = {
  de: "Fragen? Direkt per WhatsApp",
  en: "Questions? Message us on WhatsApp",
  fr: "Des questions ? Écrivez-nous sur WhatsApp",
  it: "Domande? Scriveteci su WhatsApp",
  zh: "歡迎透過 WhatsApp 聯絡我們",
};

function getContext(pathname) {
  if (pathname.startsWith("/reservation")) return "reservation";
  if (pathname.startsWith("/takeaway")) return "takeaway";
  if (pathname.startsWith("/menu")) return "menu";
  return "general";
}

export default function WhatsAppButton() {
  const { lang } = useLanguage();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [tooltip, setTooltip] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setTooltip(true), 5000);
      const h = setTimeout(() => setTooltip(false), 12000);
      return () => { clearTimeout(t); clearTimeout(h); };
    }
  }, [show]);

  if (!show) return null;

  const context = getContext(location.pathname);
  const message = (TEMPLATES[context] && TEMPLATES[context][lang]) || TEMPLATES.general.de;
  const tooltipText = TOOLTIPS[lang] || TOOLTIPS.de;
  const waUrl = `https://wa.me/41763369557?text=${encodeURIComponent(message)}`;

  return (
    <div className={`fixed right-4 sm:right-6 z-[55] flex items-end gap-3 ${context === "takeaway" ? "bottom-20 lg:bottom-6" : "bottom-6"}`} data-testid="whatsapp-float">
      {tooltip && (
        <div
          className="bg-white text-[#075e54] text-xs px-4 py-2.5 shadow-lg max-w-[220px] leading-relaxed animate-in slide-in-from-right-2 fade-in duration-500"
          style={{ fontFamily: "var(--sans)", borderRadius: "12px 12px 0 12px" }}
          data-testid="whatsapp-tooltip"
        >
          {tooltipText}
        </div>
      )}
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tooltipText}
        className="group w-14 h-14 bg-[#25D366] flex items-center justify-center rounded-full shadow-[0_4px_24px_rgba(37,211,102,0.4)] hover:shadow-[0_6px_32px_rgba(37,211,102,0.6)] hover:scale-110 transition-all duration-300"
        data-testid="whatsapp-btn"
        onClick={() => setTooltip(false)}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
